import React, { useState, useEffect, useRef } from 'react';
// Load leaflet CSS once
if (typeof document !== 'undefined' && !document.getElementById('leaflet-css')) {
  const link = document.createElement('link');
  link.id = 'leaflet-css';
  link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  document.head.appendChild(link);
}
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Pressable,
  Animated,
  Easing,
} from 'react-native';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
const L = require('leaflet'); // eslint-disable-line
type LatLngExpression = [number, number];

const LeafletMap = MapContainer as any;
const LeafletTile = TileLayer as any;
const LeafletMarker = Marker as any;
const LeafletPopup = Popup as any;
const LeafletPolyline = Polyline as any;
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { OrderModel } from '../model/orderModel';
import { deliveryPartner } from '../model/partnerModels';
import { updateOrder } from '../query/ordersQuery';
import { fetchDeliveryPartners } from '../services/partnerService';

// Fix leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const restaurantIcon = L.divIcon({
  html: `<div style="background:#32a852;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});
const userIcon = L.divIcon({
  html: `<div style="background:#0288d1;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});
const dpIcon = L.divIcon({
  html: `<div style="background:#f59e0b;width:36px;height:36px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center"><span class="material-icons-round" style="font-size:20px;color:#fff;line-height:1">delivery_dining</span></div>`,
  iconSize: [36, 36], iconAnchor: [18, 18], className: '',
});
const selectedDpIcon = L.divIcon({
  html: `<div style="background:#8B5CF6;width:40px;height:40px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 12px rgba(139,92,246,0.6);display:flex;align-items:center;justify-content:center"><span class="material-icons-round" style="font-size:22px;color:#fff;line-height:1">delivery_dining</span></div>`,
  iconSize: [40, 40], iconAnchor: [20, 20], className: '',
});

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function fmtDist(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}
function bezierPoints(sLat: number, sLng: number, eLat: number, eLng: number, steps = 80): [number, number][] {
  const midLat = (sLat + eLat) / 2, midLng = (sLng + eLng) / 2;
  const dist = Math.sqrt((eLat - sLat) ** 2 + (eLng - sLng) ** 2);
  const cpLat = midLat + dist * 0.35, cpLng = midLng;
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push([(1-t)**2*sLat + 2*(1-t)*t*cpLat + t**2*eLat, (1-t)**2*sLng + 2*(1-t)*t*cpLng + t**2*eLng]);
  }
  return pts;
}

function FitBounds({ positions }: { positions: LatLngExpression[] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
  }, [map, positions]);
  return null;
}

interface Props {
  visible: boolean;
  order: OrderModel | null;
  onClose: () => void;
}

const OrderMapModal: React.FC<Props> = ({ visible, order, onClose }) => {
  const [dpLocation, setDpLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [dpInfo, setDpInfo] = useState<deliveryPartner | null>(null);
  const [allPartners, setAllPartners] = useState<deliveryPartner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<deliveryPartner | null>(null);
  const [selectedPartnerLoc, setSelectedPartnerLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [conflictOrder, setConflictOrder] = useState<OrderModel | null>(null);
  const [conflictPartner, setConflictPartner] = useState<deliveryPartner | null>(null);
  const [showConflict, setShowConflict] = useState(false);
  const [dpActiveOrder, setDpActiveOrder] = useState<{ id: string; orderNumber?: string[] } | null>(null);
  const [partnerTripMap, setPartnerTripMap] = useState<Record<string, boolean>>({});
  const selectedPartnerUnsub = useRef<(() => void) | null>(null);

  // Slide the drawer in from the right whenever it opens
  const drawerX = useRef(new Animated.Value(1200)).current;
  useEffect(() => {
    if (visible) {
      drawerX.setValue(1200);
      Animated.timing(drawerX, { toValue: 0, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [visible, drawerX]);

  const restaurantLat = order?.sellerLocation?.latitude;
  const restaurantLng = order?.sellerLocation?.longitude;
  const userLat = order?.userLocation?.latitude;
  const userLng = order?.userLocation?.longitude;
  const hasLocations = !!(restaurantLat && restaurantLng && userLat && userLng);
  const curvedPath = hasLocations ? bezierPoints(restaurantLat!, restaurantLng!, userLat!, userLng!) : [];
  const allPositions: [number, number][] = [
    ...(restaurantLat && restaurantLng ? [[restaurantLat, restaurantLng] as [number, number]] : []),
    ...(userLat && userLng ? [[userLat, userLng] as [number, number]] : []),
    ...(dpLocation ? [[dpLocation.lat, dpLocation.lng] as [number, number]] : []),
  ];

  // Live assigned DP location
  useEffect(() => {
    if (!visible || !order?.deliveryPartnerId) { setDpLocation(null); setDpInfo(null); return; }
    const q = query(collection(db, 'deliveryPartner'), where('pincode', '==', order.deliveryPartnerId));
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const data = snap.docs[0].data() as deliveryPartner;
        setDpInfo(data);
        if (data.partnerLat && data.partnerLon) setDpLocation({ lat: data.partnerLat, lng: data.partnerLon });
      }
    });
    return () => unsub();
  }, [visible, order?.deliveryPartnerId]);

  // Check if assigned DP is in a trip (available == false)
  useEffect(() => {
    if (!visible || !order?.deliveryPartnerId) { setDpActiveOrder(null); return; }
    // dpInfo already updates via onSnapshot — derive trip status from it
  }, [visible, order?.deliveryPartnerId]);

  // Load all partners when modal opens
  useEffect(() => {
    if (!visible) return;
    fetchDeliveryPartners().then(setAllPartners);
  }, [visible]);

  // Build trip map: partners where available == false are in a trip
  useEffect(() => {
    if (!visible) { setPartnerTripMap({}); return; }
    const unsub = onSnapshot(collection(db, 'deliveryPartner'), (snap) => {
      const map: Record<string, boolean> = {};
      snap.docs.forEach(d => {
        const data = d.data() as deliveryPartner;
        if (data.pincode && data.available === false) map[data.pincode] = true;
      });
      setPartnerTripMap(map);
    });
    return () => unsub();
  }, [visible]);

  // Live location of selected partner card
  const handleSelectPartner = (partner: deliveryPartner) => {
    if (selectedPartnerUnsub.current) { selectedPartnerUnsub.current(); selectedPartnerUnsub.current = null; }
    if (selectedPartner?.pincode === partner.pincode) {
      setSelectedPartner(null);
      setSelectedPartnerLoc(null);
      return;
    }
    setSelectedPartner(partner);
    if (!partner.pincode) return;
    const q = query(collection(db, 'deliveryPartner'), where('pincode', '==', partner.pincode));
    selectedPartnerUnsub.current = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const data = snap.docs[0].data() as deliveryPartner;
        if (data.partnerLat && data.partnerLon) setSelectedPartnerLoc({ lat: data.partnerLat, lng: data.partnerLon });
        else setSelectedPartnerLoc(null);
      }
    });
  };

  useEffect(() => {
    if (!visible) {
      if (selectedPartnerUnsub.current) { selectedPartnerUnsub.current(); selectedPartnerUnsub.current = null; }
      setSelectedPartner(null);
      setSelectedPartnerLoc(null);
    }
  }, [visible]);

  const handleAssign = async (partner: deliveryPartner) => {
    if (!order?.id || !partner.pincode) return;
    setAssigning(true);
    try {
      const snap = await getDocs(query(collection(db, 'Orders'), where('deliveryPartnerId', '==', partner.pincode), where('active', '==', true)));
      const existing = snap.docs.find(d => d.id !== order.id);
      if (existing) {
        setConflictOrder({ id: existing.id, ...(existing.data() as OrderModel) });
        setConflictPartner(partner);
        setShowConflict(true);
        setAssigning(false);
        return;
      }
      await updateOrder(order.id, { deliveryPartnerId: partner.pincode });
    } catch (e) { console.error(e); }
    finally { setAssigning(false); }
  };

  const confirmAssignAnyway = async () => {
    if (!order?.id || !conflictPartner?.pincode) return;
    setAssigning(true);
    try {
      await updateOrder(order.id, { deliveryPartnerId: conflictPartner.pincode });
      setShowConflict(false);
    } catch (e) { console.error(e); }
    finally { setAssigning(false); }
  };

  const sortedPartners = allPartners
    .filter(p => p.pincode)
    .map(p => ({
      partner: p,
      dist: p.partnerLat && p.partnerLon && restaurantLat && restaurantLng
        ? haversineKm(p.partnerLat, p.partnerLon, restaurantLat, restaurantLng)
        : null,
    }))
    .sort((a, b) => {
      if (a.dist === null && b.dist === null) return 0;
      if (a.dist === null) return 1;
      if (b.dist === null) return -1;
      return a.dist - b.dist;
    });

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <Animated.View style={[styles.sheet, { transform: [{ translateX: drawerX }] }]}>

          {/* ── Header ── */}
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Order Map</Text>
              <Text style={styles.subtitle} numberOfLines={1}>{order?.orderNumber?.[0] ?? ''}</Text>
            </View>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
              <span className="material-icons-round" style={{ fontSize: 22, color: '#6B7280', lineHeight: 1 }}>close</span>
            </TouchableOpacity>
          </View>

          {/* ── Vertical scrollable body ── */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.body} showsVerticalScrollIndicator={false}>

            {/* SECTION 1: Order Route Map */}
            <View style={styles.col}>
              <View style={styles.colHeader}>
                <span className="material-icons-round" style={{ fontSize: 14, color: '#3B82F6', lineHeight: 1, marginRight: 5 }}>map</span>
                <Text style={styles.colTitle}>Order Route</Text>
                {dpInfo && <View style={[styles.dpLiveBadge, { backgroundColor: dpInfo.online ? '#dcfce7' : '#f3f4f6', marginLeft: 'auto', flexDirection: 'row', alignItems: 'center' }]}>
                  <span className="material-icons-round" style={{ fontSize: 8, color: dpInfo.online ? '#16a34a' : '#9CA3AF', lineHeight: 1, marginRight: 3 }}>circle</span>
                  <Text style={[styles.dpLiveBadgeText, { color: dpInfo.online ? '#16a34a' : '#9CA3AF' }]}>
                    {dpInfo.online ? 'Live' : 'Offline'}
                  </Text>
                </View>}
              </View>
              <View style={styles.mapWrap}>
                {hasLocations ? (
                  <LeafletMap center={[restaurantLat!, restaurantLng!]} zoom={13} style={{ width: '100%', height: '100%' }} zoomControl>
                    <LeafletTile url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                    <FitBounds positions={allPositions} />
                    <LeafletMarker position={[restaurantLat!, restaurantLng!]} icon={restaurantIcon}>
                      <LeafletPopup><span className="material-icons-round" style={{fontSize:14,verticalAlign:'middle',marginRight:4}}>storefront</span><strong>Restaurant</strong><br />{order?.sellerLocation?.name}</LeafletPopup>
                    </LeafletMarker>
                    <LeafletMarker position={[userLat!, userLng!]} icon={userIcon}>
                      <LeafletPopup><span className="material-icons-round" style={{fontSize:14,verticalAlign:'middle',marginRight:4}}>person_pin_circle</span><strong>Customer</strong><br />{order?.userLocation?.name}<br />{order?.userLocation?.address}</LeafletPopup>
                    </LeafletMarker>
                    {dpLocation && (
                      <LeafletMarker position={[dpLocation.lat, dpLocation.lng]} icon={dpIcon}>
                        <LeafletPopup><span className="material-icons-round" style={{fontSize:14,verticalAlign:'middle',marginRight:4}}>delivery_dining</span><strong>{dpInfo?.storename ?? 'DP'}</strong><br />{dpInfo?.phone}</LeafletPopup>
                      </LeafletMarker>
                    )}
                    {curvedPath.length > 0 && (
                      <LeafletPolyline positions={curvedPath} pathOptions={{ color: '#0288d1', weight: 3, dashArray: '6, 8', opacity: 0.85 }} />
                    )}
                  </LeafletMap>
                ) : (
                  <View style={styles.noLocation}><Text style={styles.noLocationText}>No location data</Text></View>
                )}
              </View>
              {/* 3 info cards */}
              <View style={styles.infoCards}>
                {/* Restaurant Card */}
                <View style={[styles.infoCard, { borderTopColor: '#22c55e' }]}>
                  <View style={styles.infoCardIcon}>
                    <span className="material-icons-round" style={{ fontSize: 16, color: '#16a34a', lineHeight: 1 }}>storefront</span>
                  </View>
                  <Text style={styles.infoCardLabel}>Restaurant</Text>
                  <Text style={styles.infoCardName} numberOfLines={2}>{order?.sellerLocation?.name ?? '—'}</Text>
                  {order?.sellerLocation?.address ? <Text style={styles.infoCardSub} numberOfLines={1}>{order.sellerLocation.address}</Text> : null}
                </View>
                {/* Customer Card */}
                <View style={[styles.infoCard, { borderTopColor: '#3b82f6' }]}>
                  <View style={styles.infoCardIcon}>
                    <span className="material-icons-round" style={{ fontSize: 16, color: '#2563eb', lineHeight: 1 }}>person_pin_circle</span>
                  </View>
                  <Text style={styles.infoCardLabel}>Customer</Text>
                  <Text style={styles.infoCardName} numberOfLines={2}>{order?.userLocation?.name ?? '—'}</Text>
                  {order?.userLocation?.address ? <Text style={styles.infoCardSub} numberOfLines={1}>{order.userLocation.address}</Text> : null}
                </View>
                {/* Delivery Partner Card */}
                {(() => {
                  const assignedEntry = sortedPartners.find(({ partner }) => partner.pincode === order?.deliveryPartnerId);
                  const dpDist = assignedEntry?.dist ?? null;
                  const distLabel = dpDist !== null
                    ? dpDist < 1 ? `${Math.round(dpDist * 1000)} m from restaurant` : `${dpDist.toFixed(1)} km from restaurant`
                    : null;
                  return (
                    <View style={[styles.infoCard, { borderTopColor: '#f59e0b' }]}>
                      <View style={styles.infoCardIcon}>
                        <span className="material-icons-round" style={{ fontSize: 16, color: '#d97706', lineHeight: 1 }}>delivery_dining</span>
                      </View>
                      <Text style={styles.infoCardLabel}>Partner</Text>
                      {dpInfo ? (
                        <>
                          <Text style={styles.infoCardName} numberOfLines={1}>{dpInfo.storename}</Text>
                          <Text style={styles.infoCardSub} numberOfLines={1}>{dpInfo.phone}</Text>
                          {distLabel && (
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                              <span className="material-icons-round" style={{ fontSize: 10, color: '#94A3B8', lineHeight: 1, marginRight: 2 }}>straighten</span>
                              <Text style={styles.infoCardSub}>{distLabel}</Text>
                            </View>
                          )}
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: dpInfo.online ? '#dcfce7' : '#f1f5f9', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                              <span className="material-icons-round" style={{ fontSize: 7, color: dpInfo.online ? '#16a34a' : '#9ca3af', lineHeight: 1, marginRight: 3 }}>circle</span>
                              <Text style={{ fontSize: 10, fontWeight: '600', color: dpInfo.online ? '#16a34a' : '#9ca3af' }}>{dpInfo.online ? 'Online' : 'Offline'}</Text>
                            </View>
                            {dpInfo.available === false ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fef3c7', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                                <span className="material-icons-round" style={{ fontSize: 10, color: '#d97706', lineHeight: 1, marginRight: 3 }}>local_shipping</span>
                                <Text style={{ fontSize: 10, fontWeight: '600', color: '#d97706' }}>In Trip</Text>
                              </View>
                            ) : (
                              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                                <span className="material-icons-round" style={{ fontSize: 10, color: '#3b82f6', lineHeight: 1, marginRight: 3 }}>check_circle</span>
                                <Text style={{ fontSize: 10, fontWeight: '600', color: '#3b82f6' }}>Free</Text>
                              </View>
                            )}
                          </View>
                        </>
                      ) : (
                        <Text style={styles.infoCardSub}>Not assigned</Text>
                      )}
                    </View>
                  );
                })()}
              </View>
            </View>

            {/* SECTION 2: Nearby Partners */}
            <View style={[styles.col, styles.colMiddle]}>
              <View style={styles.colHeader}>
                <span className="material-icons-round" style={{ fontSize: 14, color: '#8B5CF6', lineHeight: 1, marginRight: 5 }}>delivery_dining</span>
                <Text style={styles.colTitle}>Partners</Text>
                <Text style={styles.sectionCount}>{sortedPartners.length}</Text>
              </View>
              {allPartners.length === 0 ? (
                <ActivityIndicator color="#8B5CF6" style={{ marginTop: 40 }} />
              ) : (
                <View style={{ gap: 8, paddingBottom: 10 }}>
                  {sortedPartners.map(({ partner, dist }, idx) => {
                    const isAssigned = order?.deliveryPartnerId === partner.pincode;
                    const isSelected = selectedPartner?.pincode === partner.pincode;
                    return (
                      <TouchableOpacity
                        key={idx}
                        style={[styles.partnerCard, isAssigned && styles.partnerCardAssigned, isSelected && styles.partnerCardSelected]}
                        onPress={() => handleSelectPartner(partner)}
                        activeOpacity={0.85}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                          <View style={{ position: 'relative', marginRight: 8 }}>
                            <View style={[styles.partnerAvatar, { backgroundColor: isAssigned ? '#32a852' : isSelected ? '#8B5CF6' : '#e2e8f0' }]}>
                              <Text style={[styles.partnerAvatarText, { color: isAssigned || isSelected ? '#fff' : '#475569' }]}>
                                {(partner.storename ?? 'D').charAt(0).toUpperCase()}
                              </Text>
                            </View>
                            <View style={[styles.onlineDot, { position: 'absolute', bottom: 0, right: 0, backgroundColor: partner.online ? '#22c55e' : '#d1d5db' }]} />
                          </View>
                          <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                              <Text style={styles.partnerCardName} numberOfLines={1}>{partner.storename ?? 'Partner'}</Text>
                              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: partner.online ? '#dcfce7' : '#f1f5f9', borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1 }}>
                                <span className="material-icons-round" style={{ fontSize: 7, color: partner.online ? '#16a34a' : '#9ca3af', lineHeight: 1, marginRight: 2 }}>circle</span>
                                <Text style={{ fontSize: 9, fontWeight: '700', color: partner.online ? '#16a34a' : '#9ca3af' }}>{partner.online ? 'Online' : 'Offline'}</Text>
                              </View>
                            </View>
                            <Text style={styles.partnerCardPhone} numberOfLines={1}>{partner.phone ?? '—'}</Text>
                          </View>
                          {dist !== null && (
                            <View style={[styles.distBadge, { backgroundColor: dist < 3 ? '#dcfce7' : dist < 8 ? '#fef9c3' : '#fee2e2' }]}>
                              <Text style={[styles.distBadgeText, { color: dist < 3 ? '#16a34a' : dist < 8 ? '#ca8a04' : '#dc2626' }]}>{fmtDist(dist)}</Text>
                            </View>
                          )}
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          {isAssigned ? (
                            <View style={[styles.assignedBadge, { alignSelf: 'flex-start' }]}>
                              <span className="material-icons-round" style={{ fontSize: 11, color: '#16a34a', lineHeight: 1, marginRight: 3 }}>check_circle</span>
                              <Text style={styles.assignedBadgeText}>Assigned</Text>
                            </View>
                          ) : (
                            <TouchableOpacity style={[styles.assignBtn, { alignSelf: 'flex-start' }]} onPress={() => handleAssign(partner)} disabled={assigning}>
                              <span className="material-icons-round" style={{ fontSize: 12, color: '#fff', lineHeight: 1, marginRight: 3 }}>person_add</span>
                              <Text style={styles.assignBtnText}>Assign</Text>
                            </TouchableOpacity>
                          )}
                          {partner.pincode && partnerTripMap[partner.pincode] ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fef3c7', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                              <span className="material-icons-round" style={{ fontSize: 10, color: '#d97706', lineHeight: 1, marginRight: 3 }}>local_shipping</span>
                              <Text style={{ fontSize: 10, fontWeight: '600', color: '#d97706' }}>In Trip</Text>
                            </View>
                          ) : (
                            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                              <span className="material-icons-round" style={{ fontSize: 10, color: '#3b82f6', lineHeight: 1, marginRight: 3 }}>check_circle</span>
                              <Text style={{ fontSize: 10, fontWeight: '600', color: '#3b82f6' }}>Free</Text>
                            </View>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>

          </ScrollView>
        </Animated.View>
      </View>

      {/* Conflict modal */}
      <Modal visible={showConflict} transparent animationType="fade" onRequestClose={() => setShowConflict(false)}>
        <Pressable style={styles.conflictOverlay} onPress={() => setShowConflict(false)}>
          <Pressable style={styles.conflictSheet} onPress={() => {}}>
            <View style={styles.conflictHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <span className="material-icons-round" style={{ fontSize: 18, color: '#DC2626', lineHeight: 1 }}>warning</span>
                <Text style={styles.conflictTitle}>Partner Has Active Order</Text>
              </View>
              <TouchableOpacity onPress={() => setShowConflict(false)}>
                <span className="material-icons-round" style={{ fontSize: 20, color: '#6B7280', lineHeight: 1 }}>close</span>
              </TouchableOpacity>
            </View>
            <Text style={styles.conflictBody}>
              <Text style={{ fontWeight: '700' }}>{conflictPartner?.storename}</Text> is currently delivering order{' '}
              <Text style={{ fontWeight: '700' }}>#{conflictOrder?.orderNumber?.[0]}</Text>.
              {'\n'}Do you still want to assign them?
            </Text>
            <View style={styles.conflictActions}>
              <TouchableOpacity style={styles.conflictCancel} onPress={() => setShowConflict(false)}>
                <Text style={styles.conflictCancelText}>No, Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.conflictConfirm} onPress={confirmAssignAnyway} disabled={assigning}>
                {assigning ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.conflictConfirmText}>Yes, Assign Anyway</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </Modal>
  );
};

export default OrderMapModal;

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { flex: 1, height: '100%' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderBottomLeftRadius: 20, height: '100%', width: 480, maxWidth: '95%', shadowColor: '#000', shadowOffset: { width: -2, height: 0 }, shadowOpacity: 0.18, shadowRadius: 16, elevation: 14 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingTop: 14, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  title: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  subtitle: { fontSize: 12, color: '#64748B', marginTop: 2 },
  closeBtn: { padding: 6, borderRadius: 8, backgroundColor: '#F1F5F9' },
  // 3-column body
  body: { flexDirection: 'column', padding: 10, gap: 10 },
  col: { width: '100%' },
  colMiddle: { borderTopWidth: 1, borderColor: '#F1F5F9', paddingTop: 10 },
  colHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  colTitle: { fontSize: 12, fontWeight: '700', color: '#374151' },
  sectionCount: { marginLeft: 6, fontSize: 11, color: '#9CA3AF', fontWeight: '600' },
  dpLiveBadge: { marginLeft: 6, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 20 },
  dpLiveBadgeText: { fontSize: 10, fontWeight: '600' },
  mapWrap: { height: 300, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#E2E8F0' },
  noLocation: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', padding: 16 },
  noLocationText: { color: '#9CA3AF', fontSize: 12 },
  legend: { flexDirection: 'column', paddingTop: 8, gap: 4 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: '#64748B' },
  // Info cards row
  infoCards: { flexDirection: 'row', gap: 6, paddingTop: 8 },
  infoCard: { flex: 1, backgroundColor: '#F8FAFC', borderRadius: 8, padding: 8, borderWidth: 1, borderColor: '#E2E8F0', borderTopWidth: 3 },
  infoCardIcon: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', marginBottom: 4, borderWidth: 1, borderColor: '#E2E8F0' },
  infoCardLabel: { fontSize: 9, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
  infoCardName: { fontSize: 11, fontWeight: '700', color: '#1e293b', marginBottom: 1 },
  infoCardSub: { fontSize: 10, color: '#64748B' },
  // Partner cards
  partnerCard: { backgroundColor: '#F8FAFC', borderRadius: 10, padding: 10, borderWidth: 1.5, borderColor: '#E2E8F0' },
  partnerCardAssigned: { borderColor: '#22c55e', backgroundColor: '#f0fdf4' },
  partnerCardSelected: { borderColor: '#8B5CF6', backgroundColor: '#faf5ff' },
  partnerCardTop: {},
  partnerAvatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#e2e8f0', alignItems: 'center', justifyContent: 'center' },
  partnerAvatarText: { fontSize: 15, fontWeight: '700', color: '#475569' },
  onlineDot: { width: 10, height: 10, borderRadius: 5 },
  partnerCardName: { fontSize: 13, fontWeight: '700', color: '#1e293b', marginBottom: 2 },
  partnerCardPhone: { fontSize: 11, color: '#64748B', marginBottom: 6 },
  distBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, marginBottom: 6 },
  distBadgeText: { fontSize: 11, fontWeight: '700' },
  assignedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#dcfce7', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20 },
  assignedBadgeText: { fontSize: 11, fontWeight: '700', color: '#16a34a' },
  assignBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#21808D', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20 },
  assignBtnText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  selectedPartnerStrip: { flexDirection: 'row', alignItems: 'center', marginTop: 8, backgroundColor: '#faf5ff', borderRadius: 8, padding: 8, borderWidth: 1, borderColor: '#ede9fe' },
  // Conflict modal
  conflictOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  conflictSheet: { backgroundColor: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, overflow: 'hidden' },
  conflictHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  conflictTitle: { fontSize: 15, fontWeight: '700', color: '#DC2626' },
  conflictBody: { padding: 16, fontSize: 13, color: '#374151', lineHeight: 20 },
  conflictActions: { flexDirection: 'row', padding: 16, gap: 10, borderTopWidth: 1, borderTopColor: '#F1F5F9' },
  conflictCancel: { flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#F1F5F9', alignItems: 'center' },
  conflictCancelText: { fontSize: 13, fontWeight: '600', color: '#374151' },
  conflictConfirm: { flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' },
  conflictConfirmText: { fontSize: 13, fontWeight: '700', color: '#fff' },
});
